import type { SVGProps } from 'react';

import { cn } from '@/lib/utils';

/**
 * Shared prop shape for the loading-state loaders (design-bundle "Loading
 * States", Group 01 — full-page). Every loader exposes the same three knobs.
 */
export type LoaderProps = {
  /** Pixel size of the mark, or of the loader's bounding box where noted. */
  size?: number;
  className?: string;
  'aria-label'?: string;
};

type PoMarkProps = SVGProps<SVGSVGElement> & { size?: number };

/**
 * The PlanOmatic favicon mark as a bare SVG. Three paths — `p-a` (outer P +
 * dot), `p-b` (inner counter), `p-c` (bottom curve) — fill via `currentColor`
 * (defaults to `text-primary`). This is the single source of truth for the
 * mark geometry; every loading-state loader composes it rather than copying
 * the path data.
 *
 * @category Brand
 */
export const PoMark = ({ size = 88, className, ...rest }: PoMarkProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 457 456"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={cn('text-primary', className)}
    {...rest}
  >
    <path
      className="p-a"
      fill="currentColor"
      d="M95.4809 1.61123C82.2809 4.01123 73.3476 7.0779 62.0143 13.0779C32.9476 28.5446 13.2142 53.2112 4.14758 85.6112C1.21425 96.1446 1.08091 97.4779 0.680914 193.745L0.280914 291.211L5.34758 292.011C30.9476 296.011 68.1476 296.278 80.4143 292.545C85.8809 290.945 86.4143 290.411 86.0143 286.811C85.7476 284.678 85.4809 272.678 85.3476 260.278C85.2143 235.211 86.8143 223.345 93.7476 202.411C112.548 144.678 158.681 101.611 215.081 88.8112C230.414 85.3446 255.481 84.1446 271.481 86.1446C300.548 89.8779 322.414 99.4779 340.681 116.678C356.414 131.611 365.881 147.878 371.481 170.011C375.614 186.545 375.614 220.811 371.348 240.545C367.614 257.745 361.614 274.945 354.948 287.345C352.148 292.811 349.748 297.745 349.748 298.411C349.748 301.878 376.014 298.011 389.481 292.411C409.881 284.278 431.081 265.345 447.081 241.345L456.548 227.211L456.014 160.411C455.614 101.345 455.348 92.6779 453.214 85.6112C441.081 44.8112 413.481 16.6779 372.814 4.01123L361.081 0.277895L233.081 0.0112281C132.014 -0.122105 103.081 0.144561 95.4809 1.61123ZM386.814 38.5446C396.014 40.8112 404.948 50.1446 407.081 59.8779C413.614 88.6779 378.014 107.478 358.014 85.7446C346.814 73.6112 347.614 55.8779 359.748 45.2112C368.281 37.6112 376.014 35.7446 386.814 38.5446Z"
    />
    <path
      className="p-b"
      fill="currentColor"
      d="M237.081 132.545C188.681 140.145 149.748 181.611 141.081 234.945C135.881 266.278 141.881 290.278 159.348 308.411C184.681 334.811 227.081 337.345 262.014 314.411C294.148 293.478 317.348 252.011 319.881 210.945C322.281 173.878 300.548 142.545 267.081 134.545C259.748 132.811 242.548 131.611 237.081 132.545Z"
    />
    <path
      className="p-c"
      fill="currentColor"
      d="M453.081 298.278C448.548 303.345 429.614 317.611 418.681 324.278C406.281 331.745 389.614 339.078 378.414 341.745C364.814 345.078 337.614 345.745 324.148 342.945L312.414 340.678L304.148 346.545C284.281 360.678 260.014 370.678 235.081 375.078C216.681 378.278 185.748 377.078 168.414 372.545C144.414 366.278 128.281 356.945 112.948 340.545L103.748 330.678L95.6143 334.545C83.2143 340.278 70.2809 342.545 49.7476 342.545C34.0143 342.545 13.7476 340.278 3.21425 337.345L0.0142517 336.545L0.947585 348.811C4.68092 398.278 37.0809 437.611 86.4143 452.411C93.8809 454.678 103.748 454.811 223.748 455.211C366.148 455.745 362.681 455.878 384.414 447.478C412.414 436.678 438.281 410.545 448.948 382.145C455.214 365.345 456.548 354.811 456.148 323.745L455.748 295.345L453.081 298.278Z"
    />
  </svg>
);
